import { useState, useEffect } from "react";
import { AdminLayout } from "@/pages/admin/Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { Megaphone, Users, CheckCircle, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StripeBadge({ status }) {
  const map = {
    active:      { label: "Stripe activo",    bg: "#d4edda", color: "#155724" },
    pending:     { label: "Stripe pendiente", bg: "#fff3cd", color: "#856404" },
    not_created: { label: "Sin Stripe",       bg: "#e9ecef", color: "#6c757d" },
  };
  const s = map[status] || map.not_created;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 50,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color
    }}>{s.label}</span>
  );
}

export default function AdminPromoters() {
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", can_invite: true });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadPromoters(); }, []);

  const loadPromoters = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promoters`, { headers });
      setPromoters(data);
    } catch (err) {
      toast.error("Error cargando promotores");
    } finally {
      setLoading(false);
    }
  };

  const createPromoter = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Completa nombre y correo");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/create-promoter`, {
        name: form.name.trim(),
        email: form.email.trim(),
        can_invite: form.can_invite,
      }, { headers });
      toast.success(`Promotor ${form.name} creado. Se envió el correo con credenciales.`);
      setForm({ name: "", email: "", can_invite: true });
      loadPromoters();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error creando promotor");
    } finally {
      setCreating(false);
    }
  };

  const toggleCanInvite = async (promoter) => {
    const newVal = !promoter.can_invite;
    setPromoters(prev => prev.map(p => p.id === promoter.id ? { ...p, can_invite: newVal } : p));
    try {
      await axios.put(`${API}/admin/promoters/${promoter.id}/can-invite`, { can_invite: newVal }, { headers });
      toast.success(newVal ? "Permiso de invitar activado" : "Permiso de invitar revocado");
    } catch (err) {
      setPromoters(prev => prev.map(p => p.id === promoter.id ? { ...p, can_invite: !newVal } : p));
      toast.error("Error actualizando permiso");
    }
  };

  const activeStripe = promoters.filter(p => p.stripe_account_status === "active").length;
  const canInviteCount = promoters.filter(p => p.can_invite).length;

  return (
    <AdminLayout>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px 80px" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Promotores</h1>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 24 }}>
          Gestiona los promotores que invitan listeners a la plataforma.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Total promotores", value: promoters.length, icon: <Megaphone size={18} /> },
            { label: "Stripe activo",    value: activeStripe,     icon: <CheckCircle size={18} /> },
            { label: "Pueden invitar",   value: canInviteCount,   icon: <Users size={18} /> },
          ].map(s => (
            <Card key={s.label}>
              <CardContent style={{ padding: "16px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ color: "#2F5951" }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card style={{ marginBottom: 24 }}>
          <CardHeader>
            <CardTitle style={{ fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <UserPlus size={16} /> Crear nuevo promotor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <Input
                placeholder="Nombre completo"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                style={{ flex: 1, minWidth: 160 }}
              />
              <Input
                placeholder="Correo electrónico"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                style={{ flex: 1, minWidth: 200 }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <Switch
                checked={form.can_invite}
                onCheckedChange={v => setForm(f => ({ ...f, can_invite: v }))}
              />
              <span style={{ fontSize: 14 }}>Puede invitar listeners</span>
            </div>
            <Button
              onClick={createPromoter}
              disabled={creating}
              style={{ background: "#2F5951", color: "white", borderRadius: 50 }}
            >
              {creating ? "Creando…" : "Crear promotor"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: 16 }}>Promotores activos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p style={{ color: "#6b7280", fontSize: 14 }}>Cargando…</p>
            ) : promoters.length === 0 ? (
              <p style={{ color: "#6b7280", fontSize: 14, textAlign: "center", padding: "20px 0" }}>
                No hay promotores aún. Crea el primero arriba.
              </p>
            ) : (
              promoters.map((p, i) => (
                <div key={p.id} style={{
                  borderBottom: i < promoters.length - 1 ? "1px solid #f0f0f0" : "none",
                  padding: "14px 0"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                      <div style={{ color: "#6b7280", fontSize: 13 }}>{p.email}</div>
                    </div>
                    <StripeBadge status={p.stripe_account_status} />
                  </div>
                  <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      <strong style={{ color: "#2F5951" }}>{p.commission_total_sessions || 0}</strong> sesiones
                    </span>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      <strong style={{ color: "#2F5951" }}>${(p.commission_total_earned_usd || 0).toFixed(2)}</strong> USD ganados
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                    <Switch
                      checked={!!p.can_invite}
                      onCheckedChange={() => toggleCanInvite(p)}
                    />
                    <span style={{ fontSize: 13, color: p.can_invite ? "#2F5951" : "#9ca3af" }}>
                      {p.can_invite ? "Puede invitar listeners" : "Sin permiso de invitar"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
